import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Fade,
  Skeleton,
  Pagination,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Popover,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import TableChartIcon from "@mui/icons-material/TableChart";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import styles from "../AllEnquiries.module.scss";
import FilterBuilder from "../../Components/reports/FilterBuilder";
import TemplateModal from "../../Components/reports/TemplateModal";
import {
  createTemplate,
  exportReport,
  fetchReportSchema,
  listTemplates,
  runReport,
} from "../../Components/reportsApi";
import { toast } from "react-toastify";
import { formatReportCellValue } from "../../utils/datetimeIst";

const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { boxShadow: "none" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
  },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

function titleCaseField(id) {
  return String(id || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function inferReportTypeFromBase(base) {
  if (base === "course") return "courses";
  if (base === "user") return "staff";
  if (base === "program") return "programs";
  return "students";
}

function normalizeTemplateColumns(cols) {
  if (!cols || !cols.length) return [];
  if (typeof cols[0] === "object" && cols[0]?.field) {
    return cols.map((c) => c.field).filter(Boolean);
  }
  return cols.map(String);
}

function layoutKey(item) {
  return `${item.kind}:${item.id}`;
}

function defaultLabelForItem(item, helperLabels) {
  if (item.label && String(item.label).trim()) return String(item.label).trim();
  if (item.kind === "helper") return helperLabels[item.id] || titleCaseField(item.id);
  return titleCaseField(item.id);
}

function compareCellValues(a, b) {
  if (a === b) return 0;
  if (a === undefined || a === null || a === "") return 1;
  if (b === undefined || b === null || b === "") return -1;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== "" && String(b).trim() !== "") {
    return na - nb;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

const DATE_PRESETS = [
  { value: "", label: "Any time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_year", label: "This year" },
  { value: "last_year", label: "Last year" },
  { value: "custom", label: "Custom range" },
];

const MANY_SUPPORT_FIELD_MAP = {
  status: new Set(["status", "status_time_assigned"]),
  enrollment: new Set([
    "course",
    "enrollment_id",
    "enrollment_status",
    "enrollment_created_at",
  ]),
  payment_plan: new Set([
    "payment_plan_id",
    "payment_plan_status",
    "payment_plan_total_amount",
    "payment_plan_created_at",
  ]),
  payment_transaction: new Set([
    "payment_transaction_id",
    "payment_transaction_amount",
    "payment_transaction_payment_method",
    "payment_transaction_created_at",
  ]),
  enquiry: new Set([
    "enrollment_id",
    "enrollment_status",
    "enrollment_created_at",
    "enquiry_id",
    "enquiry_name",
    "enquiry_email",
    "enquiry_phone_number",
    "enquiry_created_at",
    "enquiry_converted_at",
  ]),
};

const DEFAULT_EXPAND_SUPPORT_BY_BASE = {
  enquiry: ["status", "enrollment", "payment_plan", "payment_transaction"],
  course: ["enquiry"],
  user: [],
  program: [],
};

function isManySupportField(fieldId) {
  return Object.values(MANY_SUPPORT_FIELD_MAP).some((set) => set.has(fieldId));
}

/** Prefer previous support if it still fits all selected expand-style fields; else best coverage by field count. */
function inferExpandSupportForManyFields(manyFieldIds, expandSupportOptions, previousSupport) {
  if (!manyFieldIds.length || !expandSupportOptions.length) return "";
  const allowed = (s) => MANY_SUPPORT_FIELD_MAP[s] || new Set();
  if (
    previousSupport &&
    expandSupportOptions.includes(previousSupport) &&
    manyFieldIds.every((f) => allowed(previousSupport).has(f))
  ) {
    return previousSupport;
  }
  let best = expandSupportOptions[0] || "";
  let bestCount = -1;
  for (const opt of expandSupportOptions) {
    const count = manyFieldIds.filter((f) => allowed(opt).has(f)).length;
    if (count > bestCount) {
      bestCount = count;
      best = opt;
    }
  }
  return best;
}

function columnLayoutsEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].kind !== b[i].kind || a[i].id !== b[i].id || a[i].label !== b[i].label) return false;
  }
  return true;
}

function SortableColumnHeader({ sortableId, col, clientSort, onHeaderSort, onRenameClick, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
  };
  const active = clientSort?.key === col.key;
  return (
    <TableCell
      sx={{
        fontWeight: 600,
        minWidth: 140,
        maxWidth: 280,
        verticalAlign: "top",
        borderBottom: 2,
        borderColor: "divider",
        backgroundColor: "var(--table-header, rgba(0,0,0,0.04))",
      }}
    >
      <Box ref={setNodeRef} style={style}>
        <Stack direction="row" alignItems="flex-start" spacing={0.25}>
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder column"
          sx={{ cursor: "grab", mt: -0.25 }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{ flex: 1, minWidth: 0, cursor: "pointer", py: 0.5 }}
          onClick={() => onHeaderSort(col.key)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onHeaderSort(col.key);
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ lineHeight: 1.3, wordBreak: "break-word" }}>
            {col.label}
            {active ? (clientSort.direction === "asc" ? " ▲" : " ▼") : ""}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {col.key}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Rename column" onClick={(e) => onRenameClick(e, col)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" aria-label="Remove column" onClick={() => onRemove(col)}>
          <CloseIcon fontSize="small" />
        </IconButton>
        </Stack>
      </Box>
    </TableCell>
  );
}

function ReportBuilderPage() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [schema, setSchema] = useState({});
  const [reportTypeId, setReportTypeId] = useState("students");
  /** @type {{ kind: 'field'|'helper', id: string, label?: string|null }[]} */
  const [columnLayout, setColumnLayout] = useState([]);
  const [fieldSearch, setFieldSearch] = useState("");
  const [metricsSearch, setMetricsSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedMetrics, setExpandedMetrics] = useState(false);
  const [renameAnchor, setRenameAnchor] = useState(null);
  const [renameCol, setRenameCol] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [clientSort, setClientSort] = useState(null);
  const [datePreset, setDatePreset] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [relationshipMode, setRelationshipMode] = useState("aggregate");
  const [expandSupport, setExpandSupport] = useState("");
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState([{ field: "created_at", direction: "desc" }]);
  const [rows, setRows] = useState([]);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showTemplateSave, setShowTemplateSave] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(true);
  const [hasRunOnce, setHasRunOnce] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  const base = useMemo(() => {
    const rt = schema.report_types?.find((r) => r.id === reportTypeId);
    return rt?.base || "enquiry";
  }, [schema, reportTypeId]);

  const fieldGroups = useMemo(() => schema.field_groups?.[base] || {}, [schema, base]);
  const helpersForBase = useMemo(() => schema.helpers?.[base] || [], [schema, base]);
  const helperLabels = schema.helper_labels || {};
  const dateFieldName = schema.date_field || "created_at";
  const expandSupportOptions = useMemo(() => {
    const fromSchema = schema.expand_support_by_base?.[base];
    if (Array.isArray(fromSchema) && fromSchema.length) return fromSchema;
    return DEFAULT_EXPAND_SUPPORT_BY_BASE[base] || [];
  }, [schema.expand_support_by_base, base]);

  const flatFields = useMemo(
    () => [...new Set(Object.values(fieldGroups).flat())],
    [fieldGroups]
  );

  const selectedColumns = useMemo(
    () => columnLayout.filter((c) => c.kind === "field").map((c) => c.id),
    [columnLayout]
  );
  const selectedHelpers = useMemo(
    () => columnLayout.filter((c) => c.kind === "helper").map((c) => c.id),
    [columnLayout]
  );

  const displayColumns = useMemo(
    () =>
      columnLayout.map((item) => ({
        key: item.id,
        kind: item.kind,
        sortId: layoutKey(item),
        label: defaultLabelForItem(item, helperLabels),
      })),
    [columnLayout, helperLabels]
  );

  const normalizedFieldSearch = fieldSearch.trim().toLowerCase();
  const normalizedMetricsSearch = metricsSearch.trim().toLowerCase();

  const filterIdsBySearch = useCallback((ids, q) => {
    if (!q) return ids;
    return ids.filter(
      (fid) =>
        fid.toLowerCase().includes(q) || titleCaseField(fid).toLowerCase().includes(q)
    );
  }, []);

  const sortedRows = useMemo(() => {
    if (!clientSort || !rows.length) return rows;
    const { key, direction } = clientSort;
    const mul = direction === "asc" ? 1 : -1;
    return [...rows].sort((ra, rb) => mul * compareCellValues(ra[key], rb[key]));
  }, [rows, clientSort]);

  useEffect(() => {
    const load = async () => {
      try {
        const [schemaRes, templateRes] = await Promise.all([
          fetchReportSchema(),
          listTemplates(),
        ]);
        setSchema(schemaRes);
        setTemplates(templateRes.templates || []);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || "Failed to load report builder");
      }
    };
    load();
  }, []);

  useEffect(() => {
    const fieldIds = columnLayout.filter((c) => c.kind === "field").map((c) => c.id);
    const many = fieldIds.filter(isManySupportField);

    if (!many.length || !expandSupportOptions.length) {
      setRelationshipMode((m) => (m === "aggregate" ? m : "aggregate"));
      setExpandSupport((s) => (s === "" ? s : ""));
      return;
    }

    const nextSupport = inferExpandSupportForManyFields(many, expandSupportOptions, expandSupport);
    const allowed = MANY_SUPPORT_FIELD_MAP[nextSupport] || new Set();
    const nextLayout = columnLayout.filter((c) => {
      if (c.kind === "helper") return false;
      if (c.kind === "field" && isManySupportField(c.id) && !allowed.has(c.id)) return false;
      return true;
    });

    setRelationshipMode("expand");
    if (nextSupport !== expandSupport) setExpandSupport(nextSupport);
    if (!columnLayoutsEqual(nextLayout, columnLayout)) setColumnLayout(nextLayout);
  }, [columnLayout, expandSupportOptions, expandSupport]);

  const buildDateFilter = useCallback(() => {
    if (datePreset === "custom") {
      if (!customStart || !customEnd) return null;
      return {
        field: dateFieldName,
        type: "custom",
        start_date: customStart,
        end_date: customEnd,
      };
    }
    if (datePreset) {
      return { field: dateFieldName, type: "relative", value: datePreset };
    }
    return null;
  }, [datePreset, customStart, customEnd, dateFieldName]);

  const buildColumnLayoutPayload = useCallback(() => {
    return columnLayout.map((c) => ({
      kind: c.kind,
      id: c.id,
      ...(c.label && String(c.label).trim() ? { label: String(c.label).trim() } : {}),
    }));
  }, [columnLayout]);

  const buildConfig = useCallback(
    () => ({
      base,
      report_type: reportTypeId,
      columns: selectedColumns,
      helpers: selectedHelpers,
      relationship_mode: relationshipMode,
      expand_support: relationshipMode === "expand" ? expandSupport || undefined : undefined,
      column_layout: buildColumnLayoutPayload(),
      filters,
      sort,
      date_filter: buildDateFilter(),
      page,
      page_size: pageSize,
    }),
    [
      base,
      reportTypeId,
      selectedColumns,
      selectedHelpers,
      relationshipMode,
      expandSupport,
      buildColumnLayoutPayload,
      filters,
      sort,
      buildDateFilter,
      page,
      pageSize,
    ]
  );

  const handleRun = async (targetPage = page, nextPageSize = pageSize) => {
    setError("");
    setLoading(true);
    try {
      const cfg = {
        base,
        report_type: reportTypeId,
        columns: selectedColumns,
        helpers: selectedHelpers,
        relationship_mode: relationshipMode,
        expand_support: relationshipMode === "expand" ? expandSupport || undefined : undefined,
        column_layout: buildColumnLayoutPayload(),
        filters,
        sort,
        date_filter: buildDateFilter(),
        page: targetPage,
        page_size: nextPageSize,
      };
      const data = await runReport(cfg, targetPage, nextPageSize);
      setRows(data.rows || []);
      setInsights(data.insights || null);
      setTotalPages(data.pages || 1);
      setTotalCount(typeof data.total === "number" ? data.total : 0);
      setPage(targetPage);
      setHasRunOnce(true);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Run failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (fieldId) => {
    setColumnLayout((prev) => {
      const exists = prev.some((c) => c.kind === "field" && c.id === fieldId);
      if (exists) return prev.filter((c) => !(c.kind === "field" && c.id === fieldId));
      return [...prev, { kind: "field", id: fieldId, label: null }];
    });
  };

  const toggleHelper = (hid) => {
    setColumnLayout((prev) => {
      const exists = prev.some((c) => c.kind === "helper" && c.id === hid);
      if (exists) return prev.filter((c) => !(c.kind === "helper" && c.id === hid));
      return [...prev, { kind: "helper", id: hid, label: null }];
    });
  };

  const handleReportTypeChange = (nextId) => {
    setReportTypeId(nextId);
    setColumnLayout([]);
    setFieldSearch("");
    setMetricsSearch("");
    setExpandedGroups({});
    setExpandedMetrics(false);
    setClientSort(null);
    setDatePreset("");
    setCustomStart("");
    setCustomEnd("");
    setPage(1);
  };

  const handleSaveTemplate = async (name) => {
    try {
      await createTemplate(name, buildConfig());
      const refreshed = await listTemplates();
      setTemplates(refreshed.templates || []);
      setShowTemplateSave(false);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to save template");
    }
  };

  const applyConfigFromTemplate = (cfg) => {
    const b = cfg.base || "enquiry";
    setReportTypeId(cfg.report_type || inferReportTypeFromBase(b));
    const layout = cfg.column_layout;
    if (Array.isArray(layout) && layout.length) {
      setColumnLayout(
        layout.map((x) => ({
          kind: x.kind === "helper" ? "helper" : "field",
          id: String(x.id),
          label: x.label != null && String(x.label).trim() ? String(x.label).trim() : null,
        }))
      );
    } else {
      const cols = normalizeTemplateColumns(cfg.columns);
      const hs = cfg.helpers || [];
      setColumnLayout([
        ...cols.map((id) => ({ kind: "field", id, label: null })),
        ...hs.map((id) => ({ kind: "helper", id, label: null })),
      ]);
    }
    setFilters(cfg.filters || []);
    setSort(cfg.sort || [{ field: "created_at", direction: "desc" }]);
    const df = cfg.date_filter;
    if (df?.type === "custom") {
      setDatePreset("custom");
      setCustomStart(String(df.start_date || "").slice(0, 10));
      setCustomEnd(String(df.end_date || "").slice(0, 10));
    } else if (df?.type === "relative" && df?.value) {
      setDatePreset(df.value);
      setCustomStart("");
      setCustomEnd("");
    } else {
      setDatePreset("");
      setCustomStart("");
      setCustomEnd("");
    }
    setClientSort(null);
    setPage(1);
  };

  const loadTemplate = () => {
    const tpl = templates.find((t) => String(t.id) === selectedTemplate);
    if (!tpl?.config) return;
    applyConfigFromTemplate(tpl.config);
  };

  const handleExport = async (format) => {
    try {
      const { page: _p, page_size: _ps, ...rest } = buildConfig();
      await exportReport(rest, format);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Export failed");
    }
  };

  const cellValue = (row, col) => {
    return formatReportCellValue(row[col.key]);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setColumnLayout((items) => {
      const ids = items.map((c) => layoutKey(c));
      const oldIndex = ids.indexOf(active.id);
      const newIndex = ids.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const onHeaderSort = (key) => {
    setClientSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  };

  const openRename = (e, col) => {
    const item = columnLayout.find((c) => c.kind === col.kind && c.id === col.key);
    setRenameAnchor(e.currentTarget);
    setRenameCol(col);
    setRenameDraft(item?.label || col.label || "");
  };

  const closeRename = () => {
    setRenameAnchor(null);
    setRenameCol(null);
    setRenameDraft("");
  };

  const saveRename = () => {
    if (!renameCol) return;
    const trimmed = renameDraft.trim();
    setColumnLayout((prev) =>
      prev.map((c) =>
        c.kind === renameCol.kind && c.id === renameCol.key
          ? { ...c, label: trimmed || null }
          : c
      )
    );
    closeRename();
  };

  const removeColumn = (col) => {
    setColumnLayout((prev) => prev.filter((c) => !(c.kind === col.kind && c.id === col.key)));
    setClientSort((s) => (s?.key === col.key ? null : s));
  };

  const searchingFields = normalizedFieldSearch.length > 0;
  const searchingMetrics = normalizedMetricsSearch.length > 0;

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root} sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            width: "100%",
            gap: { xs: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              flex: { md: "1 1 80%" },
              minWidth: 0,
              pr: { xs: 0, md: 0 },
              order: { xs: 2, md: 1 },
            }}
          >
            <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Report Builder
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Choose a report type and columns in Report setup, add filters if you need them, then run the
                      report to preview results.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box className={styles.header}>
                <Box sx={{ flex: 1, minWidth: 0 }} />
                <Box className={styles.headerActions} sx={{ flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleRun(1, pageSize)}
                    disabled={loading || !columnLayout.length}
                  >
                    Run Report
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => setShowTemplateSave(true)}
                    disabled={!columnLayout.length}
                  >
                    Save Template
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport("csv")}
                    disabled={!columnLayout.length}
                  >
                    CSV
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableChartIcon />}
                    onClick={() => handleExport("excel")}
                    disabled={!columnLayout.length}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SmartToyIcon />}
                    onClick={() => toast.info("Coming soon")}
                  >
                    AI Panel
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="report-template-label">Template</InputLabel>
                    <Select
                      labelId="report-template-label"
                      label="Template"
                      value={selectedTemplate}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select…</em>
                      </MenuItem>
                      {templates.map((tpl) => (
                        <MenuItem key={tpl.id} value={String(tpl.id)}>
                          {tpl.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="contained" size="small" onClick={loadTemplate} disabled={!selectedTemplate}>
                    Load
                  </Button>
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterOpen(!filterOpen)}
                    variant="outlined"
                    size="small"
                  >
                    {filterOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                </Box>
              </Box>

              {filterOpen ? (
                <Fade in={filterOpen}>
                  <FilterPaper variant="outlined">
                    <FilterBuilder filters={filters} setFilters={setFilters} selectableFields={flatFields} />
                  </FilterPaper>
                </Fade>
              ) : null}

              {error && (
                <Typography color="error" align="center" sx={{ my: 2 }}>
                  {error}
                </Typography>
              )}

              {hasRunOnce && !loading && columnLayout.length > 0 && (
                <Box className={styles.pagination}>
                  <Typography variant="body2" color="text.secondary">
                    Showing{" "}
                    {totalCount === 0
                      ? 0
                      : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)}`}{" "}
                    of {totalCount} results
                    {clientSort ? " · sorted on this page" : ""}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Per page</InputLabel>
                      <Select
                        value={pageSize}
                        label="Per page"
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setPageSize(n);
                          setPage(1);
                          handleRun(1, n);
                        }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Pagination
                      count={Math.max(totalPages, 1)}
                      page={page}
                      onChange={(_, p) => handleRun(p, pageSize)}
                      color="primary"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                    />
                  </Stack>
                </Box>
              )}

              {columnLayout.length === 0 ? (
                <Paper
                  variant="outlined"
                  className={styles.tableContainer}
                  sx={{
                    p: { xs: 2.5, sm: 3.5 },
                    borderRadius: 2,
                    bgcolor: (t) =>
                      t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    How to build a report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 560 }}>
                    Start by adding at least one field or metric. After you run the report, matching rows appear in the
                    preview table in place of these instructions.
                  </Typography>
                  <Stack component="ol" spacing={1.25} sx={{ m: 0, pl: 2.25, listStyle: "decimal" }}>
                    <Typography component="li" variant="body2" sx={{ display: "list-item", pl: 0.5 }}>
                      Open <strong>Report setup</strong> on the right and choose a <strong>report type</strong> (for
                      example students, courses, or staff).
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ display: "list-item", pl: 0.5 }}>
                      Under <strong>Fields</strong>, search or browse groups and check each column you want. Add{" "}
                      <strong>Metrics</strong> if you need counts or rollups.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ display: "list-item", pl: 0.5 }}>
                      Optionally use <strong>Show Filters</strong> and the date options to narrow rows.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ display: "list-item", pl: 0.5 }}>
                      Click <strong>Run Report</strong>. You can drag column headers to reorder them, rename them, or
                      export to CSV or Excel.
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5 }}>
                    You can also load a saved <strong>Template</strong>. <strong>AI Panel</strong> is coming soon.
                  </Typography>
                </Paper>
              ) : (
                <Paper variant="outlined" className={styles.tableContainer} sx={{ overflowX: "auto" }}>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <Table stickyHeader size="small" className={styles.enquiriesTable}>
                      <TableHead>
                        <TableRow>
                          <SortableContext
                            items={displayColumns.map((c) => c.sortId)}
                            strategy={horizontalListSortingStrategy}
                          >
                            {displayColumns.map((col) => (
                              <SortableColumnHeader
                                key={col.sortId}
                                sortableId={col.sortId}
                                col={col}
                                clientSort={clientSort}
                                onHeaderSort={onHeaderSort}
                                onRenameClick={openRename}
                                onRemove={removeColumn}
                              />
                            ))}
                          </SortableContext>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading &&
                          [...Array(5)].map((_, i) => (
                            <TableRow key={`sk-${i}`}>
                              {displayColumns.map((col, j) => (
                                <TableCell key={`skc-${i}-${j}`}>
                                  <Skeleton variant="text" width="100%" height={28} />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        {!loading && rows.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={Math.max(displayColumns.length, 1)}
                              align="center"
                              sx={{ py: 5, borderBottom: "none" }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {!hasRunOnce
                                  ? "Preview — select fields and click Run Report."
                                  : "No rows match this report."}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {!loading &&
                          sortedRows.length > 0 &&
                          sortedRows.map((row, ridx) => (
                            <TableRow key={`row-${ridx}`} hover className={styles.tableRow}>
                              {displayColumns.map((col) => (
                                <TableCell key={`cell-${ridx}-${col.sortId}`}>
                                  {cellValue(row, col)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </DndContext>
                </Paper>
              )}

              {hasRunOnce && !loading && columnLayout.length > 0 && (
                <Box className={styles.pagination}>
                  <Typography variant="body2" color="text.secondary">
                    Showing{" "}
                    {totalCount === 0
                      ? 0
                      : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)}`}{" "}
                    of {totalCount} results
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Per page</InputLabel>
                      <Select
                        value={pageSize}
                        label="Per page"
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setPageSize(n);
                          setPage(1);
                          handleRun(1, n);
                        }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Pagination
                      count={Math.max(totalPages, 1)}
                      page={page}
                      onChange={(_, p) => handleRun(p, pageSize)}
                      color="primary"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                    />
                  </Stack>
                </Box>
              )}

              {insights && hasRunOnce && !loading && columnLayout.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Insights
                  </Typography>
                  <Typography variant="body2">{insights.message}</Typography>
                </Paper>
              )}
            </Paper>
          </Box>

          <Box
            component="aside"
            aria-label="Report setup"
            sx={{
              width: { xs: "100%", md: builderOpen ? "20%" : "56px" },
              flex: { md: builderOpen ? "0 0 20%" : "0 0 56px" },
              minWidth: { md: builderOpen ? 260 : 56 },
              order: { xs: 1, md: 2 },
              display: "flex",
              flexDirection: "column",
              position: { md: "sticky" },
              top: { md: 0 },
              alignSelf: { md: "flex-start" },
              height: { xs: "auto", md: "calc(100vh - 48px)" },
              maxHeight: { xs: "none", md: "calc(100vh - 48px)" },
              borderLeft: { md: (t) => `1px solid ${t.palette.divider}` },
              borderTop: { xs: (t) => `1px solid ${t.palette.divider}`, md: "none" },
              bgcolor: "background.paper",
              boxShadow: { md: "-4px 0 24px rgba(0,0,0,0.06)" },
              zIndex: 0,
              transition: "width 0.2s ease, flex-basis 0.2s ease",
            }}
          >
            <Card
              sx={{
                borderRadius: 0,
                border: "none",
                boxShadow: "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                flex: 1,
                minHeight: { xs: 0, md: 0 },
                height: { xs: "auto", md: "100%" },
                maxHeight: { xs: "min(55vh, 480px)", md: "100%" },
                bgcolor: "transparent",
              }}
            >
                <CardHeader
                  title={
                    !isMobile && !builderOpen ? null : (
                      <Box
                        onClick={() => setBuilderOpen((o) => !o)}
                        sx={{ cursor: "pointer", userSelect: "none" }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Report setup
                        </Typography>
                      </Box>
                    )
                  }
                  subheader={
                    !isMobile && !builderOpen ? null : (
                      <Box
                        onClick={() => setBuilderOpen((o) => !o)}
                        sx={{ cursor: "pointer", userSelect: "none" }}
                      >
                        {builderOpen ? "Type, fields, metrics, date range" : "Collapsed — click to expand"}
                      </Box>
                    )
                  }
                  action={
                    <IconButton
                      aria-expanded={builderOpen}
                      aria-label={builderOpen ? "Collapse sidebar" : "Expand sidebar"}
                      onClick={() => setBuilderOpen((o) => !o)}
                      sx={{
                        transition: "transform 0.2s",
                      }}
                      size="small"
                    >
                      {!isMobile ? (
                        builderOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />
                      ) : (
                        <ExpandMoreIcon
                          sx={{
                            transform: builderOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </IconButton>
                  }
                  sx={{
                    flexShrink: 0,
                    backgroundColor: "var(--table-header)",
                    borderBottom: builderOpen ? 1 : 0,
                    borderColor: "divider",
                    py: 1.5,
                    px: !isMobile && !builderOpen ? 0.5 : undefined,
                    "& .MuiCardHeader-action": { alignSelf: "center", m: 0 },
                  }}
                />
                {builderOpen ? (
                  <CardContent
                    sx={{
                      pt: 2,
                      px: 2,
                      pb: 2,
                      flex: { md: "1 1 auto" },
                      flexGrow: 1,
                      minHeight: { md: 0 },
                      overflowY: "auto",
                      overflowX: "hidden",
                      maxHeight: { xs: "min(50vh, 440px)", md: "none" },
                    }}
                  >
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="report-type-label">Report type</InputLabel>
                      <Select
                        labelId="report-type-label"
                        label="Report type"
                        value={reportTypeId}
                        onChange={(e) => handleReportTypeChange(e.target.value)}
                      >
                        {(schema.report_types || []).map((rt) => (
                          <MenuItem key={rt.id} value={rt.id}>
                            {rt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {relationshipMode === "expand" && expandSupport ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Expanded rows ({titleCaseField(expandSupport)}). Remove those columns to return to aggregate
                        mode. Metrics are unavailable while expanded columns are selected.
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Aggregate mode. Selecting enrollment, payment, status, or related columns switches to expanded
                        rows automatically.
                      </Typography>
                    )}

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Date range
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="date-preset-label">Period</InputLabel>
                      <Select
                        labelId="date-preset-label"
                        label="Period"
                        value={datePreset}
                        onChange={(e) => setDatePreset(e.target.value)}
                      >
                        {DATE_PRESETS.map((p) => (
                          <MenuItem key={p.value || "any"} value={p.value}>
                            {p.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {datePreset === "custom" ? (
                      <Stack spacing={1.5} sx={{ mb: 2 }}>
                        <TextField
                          label="Start date"
                          type="date"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={customStart}
                          onChange={(e) => setCustomStart(e.target.value)}
                        />
                        <TextField
                          label="End date"
                          type="date"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={customEnd}
                          onChange={(e) => setCustomEnd(e.target.value)}
                        />
                      </Stack>
                    ) : null}

                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search fields…"
                      value={fieldSearch}
                      onChange={(e) => setFieldSearch(e.target.value)}
                      sx={{ mb: 1.5 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Groups stay collapsed until you expand them or search matches inside.
                    </Typography>

                    {Object.entries(fieldGroups).map(([groupName, fields]) => {
                      const filteredFields = filterIdsBySearch(fields, normalizedFieldSearch);
                      if (searchingFields && !filteredFields.length) return null;
                      const expanded = searchingFields || Boolean(expandedGroups[groupName]);
                      return (
                        <Accordion
                          key={groupName}
                          expanded={expanded}
                          onChange={(_, isExp) => {
                            if (searchingFields) return;
                            setExpandedGroups((prev) => ({ ...prev, [groupName]: isExp }));
                          }}
                          disableGutters
                          elevation={0}
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            mb: 1,
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>{groupName}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({filteredFields.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <FormGroup>
                              {filteredFields.map((fid) => (
                                <FormControlLabel
                                  key={fid}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedColumns.includes(fid)}
                                      onChange={() => toggleColumn(fid)}
                                    />
                                  }
                                  label={titleCaseField(fid)}
                                />
                              ))}
                            </FormGroup>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}

                    {helpersForBase.length > 0 ? (
                      <>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search metrics…"
                          value={metricsSearch}
                          onChange={(e) => setMetricsSearch(e.target.value)}
                          sx={{ mt: 1, mb: 1 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Accordion
                          expanded={searchingMetrics || expandedMetrics}
                          onChange={(_, isExp) => {
                            if (searchingMetrics) return;
                            setExpandedMetrics(isExp);
                          }}
                          disableGutters
                          elevation={0}
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            mb: 1,
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>Metrics</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <FormGroup>
                              {helpersForBase
                                .filter((hid) => {
                                  if (!normalizedMetricsSearch) return true;
                                  const q = normalizedMetricsSearch;
                                  return (
                                    hid.toLowerCase().includes(q) ||
                                    (helperLabels[hid] || "").toLowerCase().includes(q)
                                  );
                                })
                                .map((hid) => (
                                  <FormControlLabel
                                    key={hid}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={selectedHelpers.includes(hid)}
                                        disabled={relationshipMode === "expand"}
                                        onChange={() => toggleHelper(hid)}
                                      />
                                    }
                                    label={helperLabels[hid] || titleCaseField(hid)}
                                  />
                                ))}
                            </FormGroup>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 1 }}
                            >
                              Conversion rate cannot be combined with amount spent / total revenue in one report.
                            </Typography>
                            {relationshipMode === "expand" ? (
                              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                                Helpers are disabled in expand mode.
                              </Typography>
                            ) : null}
                          </AccordionDetails>
                        </Accordion>
                      </>
                    ) : null}

                    <Typography variant="caption" color="text.secondary">
                      Server sort (for query): default created date descending. Table headers sort the current page
                      only.
                    </Typography>
                  </CardContent>
                ) : null}
              </Card>
          </Box>
        </Box>

        <Popover
          open={Boolean(renameAnchor)}
          anchorEl={renameAnchor}
          onClose={closeRename}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, minWidth: 260 }}>
            <Typography variant="subtitle2" gutterBottom>
              Column display name
            </Typography>
            <TextField
              size="small"
              fullWidth
              label="Label"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              autoFocus
              sx={{ mb: 1.5 }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={closeRename}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={saveRename}>
                Save
              </Button>
            </Stack>
          </Box>
        </Popover>

        <TemplateModal
          show={showTemplateSave}
          onClose={() => setShowTemplateSave(false)}
          onSave={handleSaveTemplate}
        />
      </Box>
    </ThemeProvider>
  );
}

export default ReportBuilderPage;
